import { useState } from 'react'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Collapsible, CollapsibleTrigger, CollapsibleContent } from '@/components/ui/collapsible'
import { ChevronDown } from 'lucide-react'
import { cn } from '@/lib/utils'

interface CollapsibleSectionProps {
  title: string
  description?: string
  defaultOpen?: boolean
  actionButton?: React.ReactNode
  children: React.ReactNode
  id: string
  className?: string
}

export function CollapsibleSection({
  title,
  description,
  defaultOpen = false,
  actionButton,
  children,
  id,
  className
}: CollapsibleSectionProps) {
  const [isOpen, setIsOpen] = useState(defaultOpen)

  return (
    <Card id={id} className={cn('overflow-hidden rounded-xl transition-all', isOpen ? 'shadow-lg ring-1 ring-border' : 'shadow-sm', className)}>
      <Collapsible open={isOpen} onOpenChange={setIsOpen}>
        <CardHeader className="p-4 pb-3 sm:p-6 sm:pb-4">
          <div className="flex items-center justify-between">
            <CollapsibleTrigger className="group flex-1 text-left focus:outline-none">
              <div className="flex items-center gap-3">
                <CardTitle className="text-lg font-black uppercase italic tracking-tighter transition-colors group-hover:text-primary">{title}</CardTitle>
                <div className={cn(
                  'flex h-8 w-8 items-center justify-center rounded-full border border-border bg-section transition-all duration-300',
                  isOpen ? 'rotate-180 border-primary bg-primary shadow-md' : 'group-hover:bg-accent'
                )}>
                  <ChevronDown
                    className={cn(
                      'h-4 w-4 transition-colors',
                      isOpen ? 'text-primary-foreground' : 'text-muted-foreground group-hover:text-foreground'
                    )}
                  />
                </div>
              </div>
              {description && (
                <CardDescription className="mt-2 text-xs font-medium uppercase tracking-widest">{description}</CardDescription>
              )}
            </CollapsibleTrigger>
            {actionButton && (
              <div className="ml-4" onClick={(e) => e.stopPropagation()}>
                {actionButton}
              </div>
            )}
          </div>
        </CardHeader>
        <CollapsibleContent>
          <CardContent className="p-4 pt-0 animate-in slide-in-from-top-4 duration-300 sm:p-6 sm:pt-0">
            <div className="border-t border-border pt-4">
              {children}
            </div>
          </CardContent>
        </CollapsibleContent>
      </Collapsible>
    </Card>
  )
}
